import { useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { isEmbed } from '../../utils/embed';
import NotificationBell from './NotificationBell';
import {
  HiOutlineHome, HiOutlineUser, HiOutlineSparkles, HiOutlineClock,
  HiOutlineUsers, HiOutlineBookOpen, HiOutlineFolder, HiOutlineAdjustments,
  HiOutlineLogout, HiOutlineMenu, HiOutlineTag, HiOutlineCog,
  HiOutlineSwitchHorizontal, HiOutlineFilm, HiOutlinePencilAlt,
  HiOutlineAcademicCap, HiOutlineChevronDown
} from 'react-icons/hi';

const partnerNav = [
  { path: '/dashboard', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/profile', label: 'Brand Profile', icon: HiOutlineUser },
  { path: '/generate', label: 'Generate Asset', icon: HiOutlineSparkles },
  { path: '/history', label: 'Asset History', icon: HiOutlineClock },
];

const adminNav = [
  { path: '/admin', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/admin/partners', label: 'Partners', icon: HiOutlineUsers },
  { path: '/admin/users', label: 'Team Members', icon: HiOutlineBookOpen },
  { path: '/admin/topics', label: 'Topics', icon: HiOutlineTag },
  { path: '/admin/library', label: 'Library', icon: HiOutlineFolder },
  { path: '/admin/skills', label: 'Skills', icon: HiOutlineAcademicCap },
  { path: '/admin/limits', label: 'Asset Limits', icon: HiOutlineAdjustments },
  { path: '/admin/settings', label: 'Settings', icon: HiOutlineCog },
];

const marketingNav = [
  { path: '/marketing', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/marketing/studio', label: 'Content Studio', icon: HiOutlinePencilAlt },
  { path: '/marketing/videos', label: 'AI Videos', icon: HiOutlineFilm },
];

const salesNav = [
  { path: '/sales', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/sales/generate', label: 'Generate Asset', icon: HiOutlineSparkles },
  { path: '/sales/history', label: 'Asset History', icon: HiOutlineClock },
];

const pmNav = [
  { path: '/pm', label: 'Dashboard', icon: HiOutlineHome },
  { path: '/pm/partners', label: 'My Partners', icon: HiOutlineUsers },
];

const roleLabels = {
  admin: 'Admin Portal',
  partner: 'Partner Marketing Portal',
  marketing: 'Marketing Portal',
  sales: 'Sales Portal',
  partner_manager: 'Partner Manager'
};

const roleHeaderLabels = {
  admin: 'Administrator',
  partner: 'Partner',
  marketing: 'Internal Marketing',
  sales: 'Sales',
  partner_manager: 'Partner Manager'
};

function getActiveNav(user) {
  if (!user) return partnerNav;
  if (user.role === 'admin') return adminNav;
  if (user.role === 'partner') return partnerNav;
  if (user.role === 'internal') {
    const active = user.activeRole;
    if (active === 'marketing') return marketingNav;
    if (active === 'sales') return salesNav;
    if (active === 'partner_manager') return pmNav;
    if (user.isMarketing) return marketingNav;
    if (user.isSales) return salesNav;
    if (user.isPartnerManager) return pmNav;
  }
  // Legacy
  if (user.role === 'marketing') return marketingNav;
  if (user.role === 'sales') return salesNav;
  return partnerNav;
}

function getActiveRoleKey(user) {
  if (!user) return 'partner';
  if (user.role === 'admin') return 'admin';
  if (user.role === 'partner') return 'partner';
  if (user.role === 'internal') {
    if (user.activeRole) return user.activeRole;
    if (user.isMarketing) return 'marketing';
    if (user.isSales) return 'sales';
    if (user.isPartnerManager) return 'partner_manager';
  }
  return user.role;
}

function getAvailableRoles(user) {
  if (!user || user.role !== 'internal') return [];
  const roles = [];
  if (user.isMarketing) roles.push({ key: 'marketing', label: 'Marketing View', path: '/marketing' });
  if (user.isSales) roles.push({ key: 'sales', label: 'Sales View', path: '/sales' });
  if (user.isPartnerManager) roles.push({ key: 'partner_manager', label: 'PM View', path: '/pm' });
  return roles;
}

export default function Layout({ children }) {
  const { user, logout, switchRole } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roleSwitcherOpen, setRoleSwitcherOpen] = useState(false);

  const navItems = getActiveNav(user);
  const activeRoleKey = getActiveRoleKey(user);
  const availableRoles = getAvailableRoles(user);
  const hasMultipleRoles = availableRoles.length > 1;

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  const handleSwitchRole = async (roleKey, path) => {
    try {
      await switchRole(roleKey);
      setRoleSwitcherOpen(false);
      navigate(path);
    } catch (err) {
      console.error('Failed to switch role:', err);
    }
  };

  // Embed mode: keep the sidebar nav (so partners can move between pages inside
  // the portal tab) but drop the role switcher and Sign Out — logout is driven by
  // the portal's own sign-out (via the ma:signout handshake). Responsive: the
  // sidebar is fixed on wide widths and collapses to a hamburger drawer on narrow.
  if (isEmbed()) {
    return (
      <div className="min-h-screen flex bg-v2-light overflow-x-hidden">
        {/* Drawer backdrop (narrow widths only) */}
        {sidebarOpen && (
          <div className="fixed inset-0 bg-black/50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
        )}

        {/* Sidebar: static on lg+, slide-in drawer below lg */}
        <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-56 bg-v2-dark text-white transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
          <div className="p-6 pb-4">
            <h1 className="text-lg font-bold">V2 Cloud</h1>
            <p className="text-blue-300 text-sm">{roleLabels[activeRoleKey] || 'Portal'}</p>
          </div>
          {/* Role switcher — team members assigned to multiple views can switch
              between them without leaving the embedded portal. */}
          {hasMultipleRoles && (
            <div className="px-4 pb-3">
              <div className="relative">
                <button
                  onClick={() => setRoleSwitcherOpen(!roleSwitcherOpen)}
                  className="flex items-center justify-between gap-2 w-full px-3 py-2 bg-white/10 rounded-lg text-sm font-medium text-white hover:bg-white/15 transition-colors"
                >
                  <span className="flex items-center gap-2 truncate">
                    <HiOutlineSwitchHorizontal className="w-4 h-4 shrink-0" />
                    {roleHeaderLabels[activeRoleKey]}
                  </span>
                  <HiOutlineChevronDown className="w-4 h-4 shrink-0" />
                </button>
                {roleSwitcherOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleSwitcherOpen(false)} />
                    <div className="absolute left-0 right-0 mt-2 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {availableRoles.map(r => (
                        <button
                          key={r.key}
                          onClick={() => handleSwitchRole(r.key, r.path)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            r.key === activeRoleKey ? 'text-v2-blue font-medium bg-blue-50' : 'text-gray-700'
                          }`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            </div>
          )}
          <nav className="px-4 space-y-1">
            {navItems.map(item => {
              const active = location.pathname === item.path;
              return (
                <Link
                  key={item.path}
                  to={item.path}
                  onClick={() => setSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                    active ? 'bg-v2-blue text-white' : 'text-gray-300 hover:bg-white/10'
                  }`}
                >
                  <item.icon className="w-5 h-5" />
                  {item.label}
                </Link>
              );
            })}
          </nav>
        </aside>

        {/* Content column */}
        <div className="flex-1 flex flex-col min-w-0">
          {/* Compact bar with the menu toggle — only below lg */}
          <div className="lg:hidden bg-white border-b border-gray-200 px-4 py-3 flex items-center gap-3">
            <button onClick={() => setSidebarOpen(true)} aria-label="Open menu">
              <HiOutlineMenu className="w-6 h-6 text-gray-600" />
            </button>
            <span className="font-semibold text-gray-800">V2 Cloud</span>
          </div>
          <main className="flex-1 p-4 lg:p-8 overflow-auto">{children}</main>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex bg-v2-light">
      {/* Mobile overlay */}
      {sidebarOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-20 lg:hidden" onClick={() => setSidebarOpen(false)} />
      )}

      {/* Sidebar */}
      <aside className={`fixed lg:static inset-y-0 left-0 z-30 w-64 bg-v2-dark text-white transform transition-transform lg:translate-x-0 ${sidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
        <div className="p-6">
          <h1 className="text-xl font-bold">V2 Cloud</h1>
          <p className="text-blue-300 text-sm">{roleLabels[activeRoleKey] || 'Portal'}</p>
        </div>

        <nav className="px-4 space-y-1">
          {navItems.map(item => {
            const active = location.pathname === item.path;
            return (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-2.5 rounded-lg transition-colors ${
                  active ? 'bg-v2-blue text-white' : 'text-gray-300 hover:bg-white/10'
                }`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </Link>
            );
          })}
        </nav>

        <div className="absolute bottom-0 w-full p-4 border-t border-white/10">
          <div className="text-sm text-gray-400 mb-2 px-4">{user?.email}</div>
          <button
            onClick={handleLogout}
            className="flex items-center gap-3 px-4 py-2.5 w-full text-gray-300 hover:bg-white/10 rounded-lg transition-colors"
          >
            <HiOutlineLogout className="w-5 h-5" />
            Sign Out
          </button>
        </div>
      </aside>

      {/* Main content */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="bg-white shadow-sm border-b border-gray-200 px-4 lg:px-8 py-4 flex items-center justify-between">
          <button className="lg:hidden" onClick={() => setSidebarOpen(true)}>
            <HiOutlineMenu className="w-6 h-6 text-gray-600" />
          </button>
          <div className="hidden lg:block" />
          <div className="flex items-center gap-3">
            <NotificationBell />
            {hasMultipleRoles && (
              <div className="relative">
                <button
                  onClick={() => setRoleSwitcherOpen(!roleSwitcherOpen)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-gray-100 rounded-lg text-sm font-medium text-gray-700 hover:bg-gray-200 transition-colors"
                >
                  <HiOutlineSwitchHorizontal className="w-4 h-4" />
                  {roleHeaderLabels[activeRoleKey]}
                </button>
                {roleSwitcherOpen && (
                  <>
                    <div className="fixed inset-0 z-40" onClick={() => setRoleSwitcherOpen(false)} />
                    <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50">
                      {availableRoles.map(r => (
                        <button
                          key={r.key}
                          onClick={() => handleSwitchRole(r.key, r.path)}
                          className={`w-full text-left px-4 py-2 text-sm hover:bg-gray-50 ${
                            r.key === activeRoleKey ? 'text-v2-blue font-medium bg-blue-50' : 'text-gray-700'
                          }`}
                        >
                          {r.label}
                        </button>
                      ))}
                    </div>
                  </>
                )}
              </div>
            )}
            {!hasMultipleRoles && (
              <div className="text-sm text-gray-500">
                {roleHeaderLabels[activeRoleKey] || activeRoleKey}
              </div>
            )}
          </div>
        </header>

        {/* Page content */}
        <main className="flex-1 p-4 lg:p-8 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
}
